import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Content } from '@wajeez/wajeez.microservices';
import { ContentEntityOutput } from '@wajeez/wajeez.microservices/dist/api.Content.clients';
import _ from 'lodash';

import {
  searchContentEntityPaginated,
  editContentEntity,
  createContentEntity,
  getContentEntityById,
  getAllContentEntitiesByType,
  getContentCollection,
  getContentparents,
  getContentEntitiesAsDataTable,
  multiblePublish,
  setPublishDate,
  setPublish,
  updateContentPlans,
  getContentEntitiesByAuthor,
  assignContentEntity,
  orderItem,
  unassignContentEntity,
  createContentEntityByType,
  getContentEntityCollectionWithMedia,
  addContentEntityByIdForHistory,
  deleteContentEntity,
  getContentEntitiesToBeExported,
  getContentEntitiesBySelectors,
  getTopSearchedPhrases,
  searchTracked,
  getContentEntityItemsById,
  getNextTitle,
  getContentDetailsById,
  searchTrackedDropdown,
  setAdminOrder,
  getContentEntityTags,
  setContentEntityTags,
  getContentDetailsByIdMultilingual,
  getContentItemDetailsByIdMultilingual,
  createContentEntityMultilingual,
  editContentEntityMultilingual,
  setExpectedPublishDate,
  getContentEntityBadge,
  setContentEntityBadge,
  getOnboardingRandomContentEntities,
  getOnboardingContentEntityDetailsById,
  getContentEntitySEOById,
  upsertContentEntitySEO,
  changeContentEntityArchiveStatus,
} from './thunks';
import { ContentEntityState } from './types';
import { Languages } from '@wajeez-web/core/utils';

export const name = 'contentEntity';

const handleContentAudioState = () => {
  const pathname = window.location.pathname;

  const idPatternFirst = /\/content\/\w+\/([\w-]+)/;
  const idPatternSecond = /\/details\/([\w-]+)/;

  const match =
    pathname.match(idPatternFirst) || pathname.match(idPatternSecond);

  let id: string | undefined;

  if (match && match.length > 1) {
    id = match[1];
  }

  const contentAudioStateLocalStorage = JSON.parse(
    localStorage.getItem('contentWithAudioState') || 'null'
  );

  if (contentAudioStateLocalStorage?.id === id) {
    return contentAudioStateLocalStorage;
  } else {
    return null;
  }
};

const initialState: ContentEntityState = {
  data: null,
  isBookRatingModalOpen: false,
  exportedData: null,
  details: null,
  detailsHistory: [],
  contentColections: {},
  contentColectionsEnglish: {},
  contentParents: [],
  authorContent: null,
  selectedCollectionData: null,
  previewData: [],
  contentEentityBySelectorData: {},
  topSearchedPhrases: [],
  searchTracked: {},
  searchTrackedDropdown: null,
  searchTrackedProps: {
    text: '',
    isSearchTrackedReinitialized: true,
    isSearchSubmitted: true,
  },

  contentEntityItems: [],
  contentWithAudioState: handleContentAudioState(),
  nextTitle: null,
  contentDetailsById: {},
  status: 'idle',
  selectedListeningChapter: 0,
  selectedReadingChapter: 0,
  publishStatus: {},
  updatingStatus: 'idle',
  collectionStatus: 'idle',
  parentStatus: 'idle',
  authorContentStatus: 'idle',
  orderStatus: 'idle',
  assignStatus: 'idle',
  selectedCollectionStatus: 'idle',
  previewStatus: 'idle',
  multiblePublishStatus: 'idle',
  updatePlansStatus: 'idle',
  exportedDataStatus: 'idle',
  contentEentityBySelectorStatus: {},
  topSearchedPhrasesStatus: 'idle',
  searchTrackedStatus: 'idle',
  searchTrackedDropdownStatus: 'idle',

  contentEntityItemsStatus: 'idle',
  nextTitleStatus: 'idle',
  contentDetailsByIdStatus: 'idle',
  setAdminOrder: {
    status: 'idle',
  },
  contentEntityTags: {
    data: [],
    status: 'idle',
  },
  setContentEntityTags: {
    status: 'idle',
  },
  detailsByIdMultilingual: {
    data: null,
    status: 'idle',
  },
  itemDetailsByIdMultilingual: {
    data: null,
    status: 'idle',
  },
  createMultilingual: {
    data: null,
    status: 'idle',
  },
  editMultilingual: {
    data: null,
    status: 'idle',
  },
  setExpectedPublishDate: {},
  contentEntityBadge: {
    data: null,
    status: 'idle',
  },
  setContentEntityBadge: {
    status: 'idle',
  },
  onboardingRandomContentEntities: {
    data: [],
    status: 'idle',
  },
  onboardingContentEntityDetailsById: {
    data: null,
    status: 'idle',
  },
  contentEntitySEOById: {
    data: null,
    status: 'idle',
  },
  upsertContentEntitySEO: {
    status: 'idle',
  },
  changeContentEntityArchiveStatus: {
    status: 'idle',
  },
};

export const getContentDetailsByIdAsync = createAsyncThunk(
  name + '/getContentDetailsById',
  getContentDetailsById
);

export const searchContentEntityPaginatedAsync = createAsyncThunk(
  name + '/searchContentEntityPaginated',
  searchContentEntityPaginated
);

export const editContentEntityAsync = createAsyncThunk(
  name + '/editContentEntity',
  editContentEntity
);

export const createContentEntityAsync = createAsyncThunk(
  name + '/createContentEntity',
  createContentEntity
);
export const getContentEntityByIdAsync = createAsyncThunk(
  name + '/getContentEntityById',
  getContentEntityById
);
export const addContentEntityByIdForHistoryAsync = createAsyncThunk(
  name + '/addContentEntityByIdForHistory',
  addContentEntityByIdForHistory
);

export const getAllContentEntitiesByTypeAsync = createAsyncThunk(
  name + '/getAllContentEntitiesByType',
  getAllContentEntitiesByType
);

export const getContentCollectionAsync = createAsyncThunk(
  name + '/getContentCollection',
  getContentCollection
);

export const getContentparentsAsync = createAsyncThunk(
  name + '/getContentparents',
  getContentparents
);

export const getContentEntitiesAsDataTableAsync = createAsyncThunk(
  name + '/getContentEntitiesAsDataTable',
  getContentEntitiesAsDataTable
);

export const multiblePublishAsync = createAsyncThunk(
  name + '/multiblePublish',
  multiblePublish
);

export const setPublishDateAsync = createAsyncThunk(
  name + '/setPublishDate',
  setPublishDate
);

export const setPublishAsync = createAsyncThunk(
  name + '/setPublish',
  setPublish
);

export const updateContentPlansAsync = createAsyncThunk(
  name + '/updateContentPlans',
  updateContentPlans
);

export const getContentEntitiesByAuthorAsync = createAsyncThunk(
  name + '/getContentEntitiesByAuthor',
  getContentEntitiesByAuthor
);

export const assignContentEntityAsync = createAsyncThunk(
  name + '/assignContentEntity',
  assignContentEntity
);

export const orderItemAsync = createAsyncThunk(name + '/orderItem', orderItem);

export const unassignContentEntityAsync = createAsyncThunk(
  name + '/unassignContentEntity',
  unassignContentEntity
);

export const createContentEntityByTypeAsync = createAsyncThunk(
  name + '/createContentEntityByType',
  createContentEntityByType
);

export const getContentCollectionEntityByIdAsync = createAsyncThunk(
  name + '/getContentCollectionEntityById',
  getContentEntityById
);

export const getContentCollectionEntityWithMediaAsync = createAsyncThunk(
  name + '/getContentCollectionEntityWithMedia',
  getContentEntityCollectionWithMedia
);

export const deleteContentEntityAsync = createAsyncThunk(
  name + '/deleteContentEntity',
  deleteContentEntity
);

export const getContentEntitiesToBeExportedAsync = createAsyncThunk(
  name + '/getContentEntitiesToBeExported',
  getContentEntitiesToBeExported
);

export const getContentEntitiesBySelectorsAsync = createAsyncThunk(
  name + '/getContentEntitiesBySelectors',
  getContentEntitiesBySelectors
);

export const getTopSearchedPhrasesAsync = createAsyncThunk(
  name + '/getTopSearchedPhrases',
  getTopSearchedPhrases
);

export const searchTrackedAsync = createAsyncThunk(
  name + '/searchTracked',
  searchTracked
);

export const searchTrackedDropdownAsync = createAsyncThunk(
  name + '/searchTrackedDropdown',
  searchTrackedDropdown
);

export const getContentEntityItemsByIdAsync = createAsyncThunk(
  name + '/getContentEntityItemsById',
  getContentEntityItemsById
);
export const getNextTitleAsync = createAsyncThunk(
  name + '/getNextTitle',
  getNextTitle
);
export const setAdminOrderAsync = createAsyncThunk(
  name + '/setAdminOrder',
  setAdminOrder
);

export const getContentEntityTagsAsync = createAsyncThunk(
  name + '/getContentEntityTags',
  getContentEntityTags
);

export const setContentEntityTagsAsync = createAsyncThunk(
  name + '/setContentEntityTags',
  setContentEntityTags
);

export const getContentDetailsByIdMultilingualAsync = createAsyncThunk(
  name + '/getContentDetailsByIdMultilingual',
  getContentDetailsByIdMultilingual
);

export const getContentItemDetailsByIdMultilingualAsync = createAsyncThunk(
  name + '/getContentItemDetailsByIdMultilingual',
  getContentItemDetailsByIdMultilingual
);

export const createContentEntityMultilingualAsync = createAsyncThunk(
  name + '/createContentEntityMultilingual',
  createContentEntityMultilingual
);

export const editContentEntityMultilingualAsync = createAsyncThunk(
  name + '/editContentEntityMultilingual',
  editContentEntityMultilingual
);

export const setExpectedPublishDateAsync = createAsyncThunk(
  name + '/setExpectedPublishDate',
  setExpectedPublishDate
);

export const getContentEntityBadgeAsync = createAsyncThunk(
  name + '/getContentEntityBadge',
  getContentEntityBadge
);

export const setContentEntityBadgeAsync = createAsyncThunk(
  name + '/setContentEntityBadge',
  setContentEntityBadge
);

export const getOnboardingRandomContentEntitiesAsync = createAsyncThunk(
  name + '/getOnboardingRandomContentEntities',
  getOnboardingRandomContentEntities
);

export const getOnboardingContentEntityDetailsByIdAsync = createAsyncThunk(
  name + '/getOnboardingContentEntityDetailsById',
  getOnboardingContentEntityDetailsById
);

export const getContentEntitySEOByIdAsync = createAsyncThunk(
  name + '/getContentEntitySEOById',
  getContentEntitySEOById
);

export const upsertContentEntitySEOAsync = createAsyncThunk(
  name + '/upsertContentEntitySEO',
  upsertContentEntitySEO
);

export const changeContentEntityArchiveStatusAsync = createAsyncThunk(
  name + '/changeContentEntityArchiveStatus',
  changeContentEntityArchiveStatus
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearDetails: (state) => {
      state.details = null;
      state.contentColections = {};
      state.contentColectionsEnglish = {};
      state.contentParents = [];
    },
    updateTableStatus: (state, action) => {
      state.status = action.payload;
    },
    clearauthorContent: (state) => {
      state.authorContent = null;
    },
    clearSelectedCollectionData: (state) => {
      state.selectedCollectionData = null;
    },
    clearContentCollection: (state, action) => {
      state.contentColections = {
        ...state.contentColections,
        [action.payload || 'all']: undefined,
      };
    },
    clearContentCollectionEnglish: (state, action) => {
      state.contentColectionsEnglish = {
        ...state.contentColectionsEnglish,
        [action.payload || 'all']: undefined,
      };
    },
    clearContentParents: (state) => {
      state.contentParents = [];
    },
    clearSearchTracked: (state) => {
      state.searchTracked = {};
    },
    clearSearchTrackedDropdown: (state) => {
      state.searchTrackedDropdown = null;
    },

    setSelectedReadingChapter: (state, action) => {
      state.selectedReadingChapter = action.payload;
    },
    setSelectedListeningChapter: (state, action) => {
      state.selectedListeningChapter = action.payload;
    },
    setContentWithAudioState: (state, action) => {
      localStorage.setItem(
        'contentWithAudioState',
        JSON.stringify(action.payload)
      );
      state.contentWithAudioState = {
        ...action.payload,
        mode: action.payload?.mode ?? Content.PlayingMode.ListiningPage,
        childId: action.payload?.childId ?? undefined,
      };
    },
    clearContentWithAudioState: (state) => {
      localStorage.removeItem('contentWithAudioState');
      state.contentWithAudioState = null;
    },
    setRatingModalState: (state, action) => {
      state.isBookRatingModalOpen = action.payload;
    },
    setSearchTrackedInit: (state, action) => {
      state.searchTrackedProps = action.payload;
    },
    clearData: (state) => {
      state.data = null;
    },
    clearContentEntityTags: (state) => {
      state.contentEntityTags.data = [];
    },
    clearDetailsByIdMultilingual: (state) => {
      state.detailsByIdMultilingual = {
        data: null,
        status: 'idle',
      };
    },
    clearItemDetailsByIdMultilingual: (state) => {
      state.itemDetailsByIdMultilingual = {
        data: null,
        status: 'idle',
      };
    },
    clearContentEntityBadge: (state) => {
      state.contentEntityBadge = {
        data: null,
        status: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getContentEntitiesAsDataTableAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        getContentEntitiesAsDataTableAsync.fulfilled,
        (state, action) => {
          state.status = 'idle';
          state.data = action.payload;
        }
      )
      .addCase(getContentEntitiesAsDataTableAsync.rejected, (state, action) => {
        state.status = 'failed';
      })
      .addCase(getContentEntityByIdAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getContentEntityByIdAsync.fulfilled, (state, action) => {
        // state.status = 'idle';
        // state.details = action.payload;
        return {
          ...state,
          status: 'idle',
          details: action.payload,
          detailsHistory: [...state.detailsHistory, action.payload],
        };
      })
      .addCase(getContentEntityByIdAsync.rejected, (state, action) => {
        state.status = 'failed';
      })
      .addCase(
        addContentEntityByIdForHistoryAsync.fulfilled,
        (state, action) => {
          return {
            ...state,
            detailsHistory: [...state.detailsHistory, action.payload],
          };
        }
      )
      .addCase(editContentEntityAsync.pending, (state) => {
        state.updatingStatus = 'loading';
      })
      .addCase(editContentEntityAsync.fulfilled, (state, action) => {
        state.updatingStatus = 'idle';
      })
      .addCase(editContentEntityAsync.rejected, (state, action) => {
        state.updatingStatus = 'failed';
      })
      .addCase(createContentEntityAsync.pending, (state) => {
        state.updatingStatus = 'loading';
      })
      .addCase(createContentEntityAsync.fulfilled, (state, action) => {
        state.updatingStatus = 'idle';
      })
      .addCase(createContentEntityAsync.rejected, (state, action) => {
        state.updatingStatus = 'failed';
      })
      .addCase(getContentCollectionAsync.pending, (state) => {
        state.collectionStatus = 'loading';
      })
      .addCase(getContentCollectionAsync.fulfilled, (state, action) => {
        state.collectionStatus = 'idle';
        if (
          action.meta.arg.languageId === Languages.AR.toString() ||
          !action.meta.arg.languageId
        ) {
          state.contentColections = {
            ...state.contentColections,
            [action.meta.arg.ContenteEntityType || 'all']: action.payload,
          };
        } else {
          state.contentColectionsEnglish = {
            ...state.contentColectionsEnglish,
            [action.meta.arg.ContenteEntityType || 'all']: action.payload,
          };
        }
      })
      .addCase(getContentCollectionAsync.rejected, (state, action) => {
        state.collectionStatus = 'failed';
      })
      .addCase(getContentparentsAsync.pending, (state) => {
        state.parentStatus = 'loading';
      })
      .addCase(getContentparentsAsync.fulfilled, (state, action) => {
        state.parentStatus = 'idle';
        state.contentParents = action.payload;
      })
      .addCase(getContentparentsAsync.rejected, (state, action) => {
        state.parentStatus = 'failed';
      })
      .addCase(getContentEntitiesByAuthorAsync.pending, (state) => {
        state.authorContentStatus = 'loading';
      })
      .addCase(getContentEntitiesByAuthorAsync.fulfilled, (state, action) => {
        state.authorContentStatus = 'idle';
        state.authorContent = action.payload;
      })
      .addCase(getContentEntitiesByAuthorAsync.rejected, (state, action) => {
        state.authorContentStatus = 'failed';
      })
      .addCase(orderItemAsync.pending, (state) => {
        state.orderStatus = 'loading';
      })
      .addCase(orderItemAsync.fulfilled, (state, action) => {
        state.orderStatus = 'idle';
      })
      .addCase(orderItemAsync.rejected, (state, action) => {
        state.orderStatus = 'failed';
      })
      .addCase(assignContentEntityAsync.pending, (state) => {
        state.assignStatus = 'loading';
      })
      .addCase(assignContentEntityAsync.fulfilled, (state, action) => {
        state.assignStatus = 'idle';
      })
      .addCase(assignContentEntityAsync.rejected, (state, action) => {
        state.assignStatus = 'failed';
      })
      .addCase(unassignContentEntityAsync.pending, (state) => {
        state.assignStatus = 'loading';
      })
      .addCase(unassignContentEntityAsync.fulfilled, (state, action) => {
        state.assignStatus = 'idle';
      })
      .addCase(unassignContentEntityAsync.rejected, (state, action) => {
        state.assignStatus = 'failed';
      })
      .addCase(getContentCollectionEntityByIdAsync.pending, (state) => {
        state.selectedCollectionStatus = 'loading';
      })
      .addCase(
        getContentCollectionEntityByIdAsync.fulfilled,
        (state, action) => {
          state.selectedCollectionStatus = 'idle';
          state.selectedCollectionData = action.payload;
        }
      )
      .addCase(
        getContentCollectionEntityByIdAsync.rejected,
        (state, action) => {
          state.selectedCollectionStatus = 'failed';
        }
      )
      .addCase(getContentCollectionEntityWithMediaAsync.pending, (state) => {
        state.previewStatus = 'loading';
      })
      .addCase(
        getContentCollectionEntityWithMediaAsync.fulfilled,
        (state, action) => {
          state.previewStatus = 'idle';
          state.previewData = action.payload;
        }
      )
      .addCase(
        getContentCollectionEntityWithMediaAsync.rejected,
        (state, action) => {
          state.previewStatus = 'failed';
        }
      )
      .addCase(setPublishAsync.pending, (state, action) => {
        state.publishStatus[action.meta.arg.id!] = 'loading';
      })
      .addCase(setPublishAsync.fulfilled, (state, action) => {
        state.publishStatus[action.meta.arg.id!] = 'idle';
      })
      .addCase(setPublishAsync.rejected, (state, action) => {
        state.publishStatus[action.meta.arg.id!] = 'failed';
      })
      .addCase(multiblePublishAsync.pending, (state) => {
        state.multiblePublishStatus = 'loading';
      })
      .addCase(multiblePublishAsync.fulfilled, (state, action) => {
        state.multiblePublishStatus = 'idle';
      })
      .addCase(multiblePublishAsync.rejected, (state, action) => {
        state.multiblePublishStatus = 'failed';
      })
      .addCase(updateContentPlansAsync.pending, (state) => {
        state.updatePlansStatus = 'loading';
      })
      .addCase(updateContentPlansAsync.fulfilled, (state, action) => {
        state.updatePlansStatus = 'idle';
      })
      .addCase(updateContentPlansAsync.rejected, (state, action) => {
        state.updatePlansStatus = 'failed';
      })
      .addCase(getContentEntitiesToBeExportedAsync.pending, (state) => {
        state.exportedDataStatus = 'loading';
      })
      .addCase(
        getContentEntitiesToBeExportedAsync.fulfilled,
        (state, action) => {
          state.exportedDataStatus = 'idle';
          state.exportedData = action.payload;
        }
      )
      .addCase(
        getContentEntitiesToBeExportedAsync.rejected,
        (state, action) => {
          state.exportedDataStatus = 'failed';
        }
      )
      .addCase(getContentEntitiesBySelectorsAsync.pending, (state, action) => {
        state.contentEentityBySelectorStatus = {
          ...state.contentEentityBySelectorStatus,
          [action.meta.arg.uniqueId]: 'loading',
        };
      })
      .addCase(
        getContentEntitiesBySelectorsAsync.fulfilled,
        (state, action) => {
          const uniqueId = Object.keys(action.payload)[0];

          if (state.contentEentityBySelectorData[uniqueId]) {
            const stateObj = _.reduce(
              state.contentEentityBySelectorData[uniqueId].data,
              function (obj: { [key: string]: ContentEntityOutput }, param) {
                if (param.id) obj[param.id] = param;

                return obj;
              },
              {}
            );

            const dataObj = _.reduce(
              action.payload[uniqueId].data,
              function (obj: { [key: string]: ContentEntityOutput }, param) {
                if (param.id) obj[param.id] = param;

                return obj;
              },
              {}
            );

            const totalObj = {
              ...stateObj,
              ...dataObj,
            };

            return {
              ...state,
              contentEentityBySelectorStatus: {
                ...state.contentEentityBySelectorStatus,
                [uniqueId]: 'idle',
              },
              contentEentityBySelectorData: {
                ...state.contentEentityBySelectorData,
                [uniqueId]: {
                  data: Object.values(totalObj),
                  meta: action.payload[uniqueId].meta,
                },
              },
            };
          } else {
            return {
              ...state,
              contentEentityBySelectorStatus: {
                ...state.contentEentityBySelectorStatus,
                [uniqueId]: 'idle',
              },
              contentEentityBySelectorData: {
                ...state.contentEentityBySelectorData,
                ...action.payload,
              },
            };
          }
        }
      )
      .addCase(getContentEntitiesBySelectorsAsync.rejected, (state, action) => {
        const actionPayload: any = action.payload;

        if (actionPayload.message !== 'canceled') {
          state.contentEentityBySelectorStatus = {
            ...state.contentEentityBySelectorStatus,
            [action.meta.arg.uniqueId]: 'failed',
          };
        }
      })
      .addCase(getTopSearchedPhrasesAsync.pending, (state) => {
        state.topSearchedPhrasesStatus = 'loading';
      })
      .addCase(getTopSearchedPhrasesAsync.fulfilled, (state, action) => {
        state.topSearchedPhrasesStatus = 'idle';
        state.topSearchedPhrases = action.payload;
      })
      .addCase(getTopSearchedPhrasesAsync.rejected, (state) => {
        state.topSearchedPhrasesStatus = 'failed';
      })
      .addCase(searchTrackedAsync.pending, (state) => {
        state.searchTrackedStatus = 'loading';
      })
      .addCase(searchTrackedAsync.fulfilled, (state, action) => {
        const uniqueId = Object.keys(action.payload)[0];

        if (state.searchTracked[uniqueId]) {
          const stateObj = _.reduce(
            state.searchTracked[uniqueId].data,
            function (obj: { [key: string]: ContentEntityOutput }, param) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const dataObj = _.reduce(
            action.payload[uniqueId].data,
            function (obj: { [key: string]: ContentEntityOutput }, param) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const totalObj = {
            ...stateObj,
            ...dataObj,
          };

          return {
            ...state,
            searchTrackedStatus: 'idle',
            searchTracked: {
              ...state.searchTracked,
              [uniqueId]: {
                data: Object.values(totalObj),
                meta: action.payload[uniqueId].meta,
              },
            },
          };
        } else {
          return {
            ...state,
            searchTrackedStatus: 'idle',
            searchTracked: {
              ...state.searchTracked,
              ...action.payload,
            },
          };
        }
      })
      .addCase(searchTrackedAsync.rejected, (state) => {
        state.searchTrackedStatus = 'failed';
      })
      .addCase(searchTrackedDropdownAsync.pending, (state) => {
        state.searchTrackedDropdownStatus = 'loading';
      })
      .addCase(searchTrackedDropdownAsync.fulfilled, (state, action) => {
        if (state.searchTrackedDropdown) {
          const stateObj = _.reduce(
            state.searchTrackedDropdown.data,
            function (obj: { [key: string]: ContentEntityOutput }, param) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const dataObj = _.reduce(
            action.payload.data,
            function (obj: { [key: string]: ContentEntityOutput }, param) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const totalObj = {
            ...stateObj,
            ...dataObj,
          };

          state.searchTrackedDropdown = {
            data: Object.values(totalObj),
            meta: action.payload.meta,
          };
          state.searchTrackedDropdownStatus = 'idle';
        } else {
          state.searchTrackedDropdown = action.payload;
          state.searchTrackedDropdownStatus = 'idle';
        }
      })
      .addCase(searchTrackedDropdownAsync.rejected, (state) => {
        state.searchTrackedDropdownStatus = 'failed';
      })

      .addCase(getContentEntityItemsByIdAsync.pending, (state) => {
        state.contentEntityItemsStatus = 'loading';
      })
      .addCase(getContentEntityItemsByIdAsync.fulfilled, (state, action) => {
        state.contentEntityItemsStatus = 'idle';
        state.contentEntityItems = action.payload;
      })
      .addCase(getContentEntityItemsByIdAsync.rejected, (state, action) => {
        state.contentEntityItemsStatus = 'failed';
      })
      .addCase(getNextTitleAsync.pending, (state) => {
        state.nextTitleStatus = 'loading';
      })
      .addCase(getNextTitleAsync.fulfilled, (state, action) => {
        state.nextTitleStatus = 'idle';
        state.nextTitle = action.payload;
      })
      .addCase(getNextTitleAsync.rejected, (state, action) => {
        state.nextTitleStatus = 'failed';
      })
      .addCase(getContentDetailsByIdAsync.pending, (state) => {
        state.contentDetailsByIdStatus = 'loading';
      })
      .addCase(getContentDetailsByIdAsync.fulfilled, (state, action) => {
        state.contentDetailsByIdStatus = 'idle';
        state.contentDetailsById = action.payload;
      })
      .addCase(getContentDetailsByIdAsync.rejected, (state) => {
        state.contentDetailsByIdStatus = 'failed';
      })
      .addCase(setAdminOrderAsync.pending, (state) => {
        state.setAdminOrder.status = 'loading';
      })
      .addCase(setAdminOrderAsync.fulfilled, (state, action) => {
        state.setAdminOrder.status = 'idle';
      })
      .addCase(setAdminOrderAsync.rejected, (state) => {
        state.setAdminOrder.status = 'failed';
      })
      .addCase(getContentEntityTagsAsync.pending, (state) => {
        state.contentEntityTags.status = 'loading';
      })
      .addCase(getContentEntityTagsAsync.fulfilled, (state, action) => {
        state.contentEntityTags = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getContentEntityTagsAsync.rejected, (state) => {
        state.contentEntityTags.status = 'failed';
      })
      .addCase(setContentEntityTagsAsync.pending, (state) => {
        state.setContentEntityTags.status = 'loading';
      })
      .addCase(setContentEntityTagsAsync.fulfilled, (state) => {
        state.setContentEntityTags.status = 'idle';
      })
      .addCase(setContentEntityTagsAsync.rejected, (state) => {
        state.setContentEntityTags.status = 'failed';
      })
      .addCase(getContentDetailsByIdMultilingualAsync.pending, (state) => {
        state.detailsByIdMultilingual.status = 'loading';
      })
      .addCase(
        getContentDetailsByIdMultilingualAsync.fulfilled,
        (state, action) => {
          state.detailsByIdMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getContentDetailsByIdMultilingualAsync.rejected, (state) => {
        state.detailsByIdMultilingual.status = 'failed';
      })
      .addCase(getContentItemDetailsByIdMultilingualAsync.pending, (state) => {
        state.itemDetailsByIdMultilingual.status = 'loading';
      })
      .addCase(
        getContentItemDetailsByIdMultilingualAsync.fulfilled,
        (state, action) => {
          state.itemDetailsByIdMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getContentItemDetailsByIdMultilingualAsync.rejected, (state) => {
        state.itemDetailsByIdMultilingual.status = 'failed';
      })
      .addCase(createContentEntityMultilingualAsync.pending, (state) => {
        state.createMultilingual.status = 'loading';
      })
      .addCase(
        createContentEntityMultilingualAsync.fulfilled,
        (state, action) => {
          state.createMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(createContentEntityMultilingualAsync.rejected, (state) => {
        state.createMultilingual.status = 'failed';
      })
      .addCase(editContentEntityMultilingualAsync.pending, (state) => {
        state.editMultilingual.status = 'loading';
      })
      .addCase(
        editContentEntityMultilingualAsync.fulfilled,
        (state, action) => {
          state.editMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(editContentEntityMultilingualAsync.rejected, (state) => {
        state.editMultilingual.status = 'failed';
      })
      .addCase(setExpectedPublishDateAsync.pending, (state, action) => {
        state.setExpectedPublishDate = {
          ...state.setExpectedPublishDate,
          [action.meta.arg.languageId!]: {
            status: 'loading',
          },
        };
      })
      .addCase(setExpectedPublishDateAsync.fulfilled, (state, action) => {
        state.setExpectedPublishDate = {
          ...state.setExpectedPublishDate,
          [action.meta.arg.languageId!]: {
            status: 'idle',
          },
        };
      })
      .addCase(setExpectedPublishDateAsync.rejected, (state, action) => {
        state.setExpectedPublishDate = {
          ...state.setExpectedPublishDate,
          [action.meta.arg.languageId!]: {
            status: 'failed',
          },
        };
      })
      .addCase(getContentEntityBadgeAsync.pending, (state) => {
        state.contentEntityBadge.status = 'loading';
      })
      .addCase(getContentEntityBadgeAsync.fulfilled, (state, action) => {
        state.contentEntityBadge = {
          data: action.payload[0],
          status: 'idle',
        };
      })
      .addCase(getContentEntityBadgeAsync.rejected, (state) => {
        state.contentEntityBadge.status = 'failed';
      })
      .addCase(setContentEntityBadgeAsync.pending, (state) => {
        state.setContentEntityBadge.status = 'loading';
      })
      .addCase(setContentEntityBadgeAsync.fulfilled, (state) => {
        state.setContentEntityBadge.status = 'idle';
      })
      .addCase(setContentEntityBadgeAsync.rejected, (state) => {
        state.setContentEntityBadge.status = 'failed';
      })
      .addCase(getOnboardingRandomContentEntitiesAsync.pending, (state) => {
        state.onboardingRandomContentEntities.status = 'loading';
      })
      .addCase(
        getOnboardingRandomContentEntitiesAsync.fulfilled,
        (state, action) => {
          state.onboardingRandomContentEntities = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getOnboardingRandomContentEntitiesAsync.rejected, (state) => {
        state.onboardingRandomContentEntities.status = 'failed';
      })
      .addCase(getOnboardingContentEntityDetailsByIdAsync.pending, (state) => {
        state.onboardingContentEntityDetailsById.status = 'loading';
      })
      .addCase(
        getOnboardingContentEntityDetailsByIdAsync.fulfilled,
        (state, action) => {
          state.onboardingContentEntityDetailsById = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getOnboardingContentEntityDetailsByIdAsync.rejected, (state) => {
        state.onboardingContentEntityDetailsById.status = 'failed';
      })
      .addCase(getContentEntitySEOByIdAsync.pending, (state) => {
        state.contentEntitySEOById.status = 'loading';
      })
      .addCase(getContentEntitySEOByIdAsync.fulfilled, (state, action) => {
        state.contentEntitySEOById = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getContentEntitySEOByIdAsync.rejected, (state) => {
        state.contentEntitySEOById.status = 'failed';
      })
      .addCase(upsertContentEntitySEOAsync.pending, (state) => {
        state.upsertContentEntitySEO.status = 'loading';
      })
      .addCase(upsertContentEntitySEOAsync.fulfilled, (state) => {
        state.upsertContentEntitySEO = {
          status: 'idle',
        };
      })
      .addCase(upsertContentEntitySEOAsync.rejected, (state) => {
        state.upsertContentEntitySEO.status = 'failed';
      })
      .addCase(changeContentEntityArchiveStatusAsync.pending, (state) => {
        state.changeContentEntityArchiveStatus.status = 'loading';
      })
      .addCase(changeContentEntityArchiveStatusAsync.fulfilled, (state) => {
        state.changeContentEntityArchiveStatus.status = 'idle';
      })
      .addCase(changeContentEntityArchiveStatusAsync.rejected, (state) => {
        state.changeContentEntityArchiveStatus.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
